<template>
  <validation-provider
    name="customer"
    rules="required"
    v-slot="{ errors }">
    <div
      :class="{
        'form-control': true,
        'is-select': true,
        'is-invalid': errors.length,
        'vs-wrap-text': true
      }">
      <v-select
        v-model="selectedCustomer"
        :disabled="disabled"
        :filter="filterCustomers"
        :options="limitedCustomers"
        :searchable="true"
        placeholder="Wybierz klienta"
        ref="customers-filter"
        label="name">
        <template slot="no-options">
          <template v-if="!emptyResults">Trwa wyszukiwanie&hellip;</template>
          <template v-else>Brak klientów pasujących do podanej frazy&hellip;</template>
        </template>
        <template
          slot="option"
          slot-scope="option">
          <div class="d-center">
            {{ option.invoice_company }}
          </div>
        </template>
        <template
          slot="selected-option"
          slot-scope="option">
          <div class="selected d-center">
            {{ option.invoice_company }}
          </div>
        </template>
      </v-select>
    </div>
    <div class="invalid-feedback">
      {{ errors[0] }}
    </div>

    <p
      v-if="isInDebt"
      class="text-danger mt-1">
      Wybrany klient jest zadłużony.
    </p>

    <div
      v-if="currentCustomer && showDiscount && hasDiscount"
      class="text-secondary">
      <div class="my-2 ml-2">
        <d-link
          class="dropdown-toggle"
          v-d-toggle.discounts-collapse
          style="font-size: 14px;"
          @click.native="handleToggle()">
          <template v-if="detailsVisible">
            Ukryj rabaty
          </template>
          <template v-else>
            Pokaż rabaty
          </template>
        </d-link>
        <d-collapse
          id="discounts-collapse"
          class="pl-2"
          accordion="sidebar-items-accordion">
          <small>
            Podstawowy:
            <strong class="text-success">{{ currentCustomer.discount }}%</strong>
          </small>
          <div v-if="currentCustomer.products.length">
            <small>Rabaty dla produktów:</small>
            <div
              v-for="(product, index) of currentCustomer.products"
              :key="'product-discount-' + index"
              class="flex pl-2">
              <small>
                <strong class="text-primary">
                  {{ getProductName(product.id) }}:
                </strong>
              </small>
              <small>
                <strong class="text-success pl-1">
                  {{ product.discount }}%
                </strong>
              </small>
            </div>
          </div>
          <div v-if="currentCustomer.manufacturers.length">
            <small>Rabaty producentów:</small>
            <div
              v-for="(discount, index) of currentCustomer.manufacturers"
              :key="'manufacturer-discount-' + index"
              class="flex pl-2">
              <small>
                <strong class="text-primary">
                  {{ getManufacturerName(discount.id) }}:
                </strong>
              </small>
              <small>
                <strong class="text-success pl-1">
                  {{ discount.discount }}%
                </strong>
              </small>
            </div>
          </div>
          <div v-if="currentCustomer.categories.length">
            <small>Rabaty dla kategorii produktów:</small>
            <div
              v-for="(discount, index) of currentCustomer.categories"
              :key="'category-discount-' + index"
              class="flex pl-2">
              <small>
                <strong class="text-primary">
                  {{ getCategoryName(discount.id) }}:
                </strong>
              </small>
              <small>
                <strong class="text-success pl-1">
                  {{ discount.discount }}%
                </strong>
              </small>
            </div>
          </div>
        </d-collapse>
      </div>
    </div>
  </validation-provider>
</template>

<script>
import FormUtils from '@/utils/FormUtils';
import Vue from 'vue';

export default {
  name: 'order-customer-select',
  props: [
    'disabled',
    'showDiscount',
    'allCategories',
    'allManufacturers',
    'allProducts',
    'customerID',
    'addressID',
    'paymentID'
  ],
  computed: {
    hasDiscount () {
      return this.currentCustomer.discount || this.currentCustomer.categories.length || this.currentCustomer.manufacturers.length || this.currentCustomer.products.length
    },
    isEdit() {
      return !!this.$route.params.id;
    },
    limitedCustomers () {
      let options = this.customers;

      if (this.$refs['customers-filter'] && this.$refs['customers-filter'].search.length < 1) {
        return options.slice(0, 100);
      }

      return options;
    },
    isInDebt () {
      if (this.currentCustomer && this.currentCustomer.debt_amount) {
        return this.currentCustomer.debt_amount > 0;
      }

      return false;
    }
  },
  data () {
    return {
      customer_id: null,
      customers: [],
      currentCustomer: null,
      emptyResults: false,
      selectedCustomer: null,
      loadError: false,
      loadingData: false,
      detailsVisible: false
    }
  },
  watch: {
    selectedCustomer (newValue) {
      if (newValue && newValue.id) {
        if (!this.loadingData) {
          let customerAddressID = null;
          let customerPaymentID = null;
          if (newValue.id === this.customerID) {
            customerAddressID = this.addressID;
            customerPaymentID = this.paymentID;
          }
          Vue.nextTick(() => {
            this.loadCustomer(newValue.id, customerAddressID, false, customerPaymentID);
          });
          return;
        }
      }

      this.selectedCustomer = null;
      this.$bus.$emit('order-update-selected-client');
      this.$bus.$emit('order-update-client-discounts');
      this.$bus.$emit('order-update-address-list');
      this.$bus.$emit('order-update-payment-list');
    }
  },
  mounted () {
    if (!this.isEdit) {
      this.loadCustomersData();
    }
  },
  methods: {
    loadCustomersData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/contacts/all-items',
        method: 'get',
        outputKey: 'customers',
        loadedKey: 'loaded',
        errorKey: 'loadError',
        successAction: () => {
          if (this.customerID) {
            this.selectedCustomer = this.customers.find(item => item.id === this.customerID);
          }
        }
      });
    },
    loadCustomer (customerID, addressID = null, skipDataReload = false, paymentID = null) {
      this.loadingData = true;
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/api/contacts/item/' + parseInt(customerID, 10),
        outputKey: 'currentCustomer',
        errorKey: 'loadError',
        wrapResponseIntoArray: false,
        successAction: () => {
          this.customer_id = parseInt(customerID, 10);

          if (!skipDataReload) {
            Vue.nextTick(() => {
              setTimeout(() => {
                this.$bus.$emit('order-update-selected-client', { customerID: this.customer_id, isInDebt: this.currentCustomer.debt_amount > 0 });
                this.$bus.$emit('order-update-client-discounts', this.customer_id);
                this.$bus.$emit('order-update-address-list', this.currentCustomer.addresses, addressID);
                this.$bus.$emit('order-update-payment-list', this.currentCustomer.payments, paymentID);
                this.loadingData = false;

                if (this.currentCustomer.payments.length > 1) {
                  this.loadCustomerOrders(parseInt(customerID, 10));
                }
              }, 0);
            });
          }
        }
      });
    },
    filterCustomers (options, search) {
      let results = options.filter(item => (item.invoice_company + ' ' + item.email + ' ' + item.phone + ' ' + item.name + ' ' + item.surname).toLocaleLowerCase('pl').indexOf(search.toLocaleLowerCase('pl')) > -1).slice(0, 100);
      if (results.length) {
        this.emptyResults = false;
      } else {
        this.emptyResults = true;
      }
      return results;
    },
    handleToggle() {
      this.detailsVisible = !this.detailsVisible;
    },
    loadCustomerOrders (customerID) {
      let where = {
        customer_id: customerID
      }
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/api/orders/items',
        errorKey: 'loadError',
        wrapResponseIntoArray: false,
        params: {
          where,
          order: {
            column: 'id',
            direction: 'ASC'
          }
        },
        successAction: (response) => {
          if (!response || !response.length) {
            return;
          }
          let customerPaymentID = response.pop().payment_id;
          if (customerID === this.customerID) {
            customerPaymentID = this.paymentID;
          }
          this.$bus.$emit('order-set-default-payment', customerPaymentID);
        }
      });
    },
    getProductName (productID) {
      let theProduct = this.allProducts.find(product => product.id === productID);

      if (theProduct) {
        return theProduct.name;
      }

      return 'Produkt o ID#' + productID + ' już nie istnieje';
    },
    getManufacturerName (manufacturerID) {
      let theManufacturer = this.allManufacturers.find(manufacturer => manufacturer.id === manufacturerID);

      if (theManufacturer) {
        return theManufacturer.name;
      }

      return 'Producent o ID#' + manufacturerID + ' już nie istnieje';
    },
    getCategoryName (categoryID) {
      let theCategory = this.allCategories.find(category => category.id === categoryID);

      if (theCategory) {
        return theCategory.name;
      }

      return 'Kategoria o ID#' + categoryID + ' już nie istnieje';
    }
  }
}
</script>
